/**
 * Open Bank Transactions List
 *
 * Displays bank transactions from TrueLayer with filtering and reconciliation.
 */

import React, { useState } from 'react';
import {
  useBankTransactions,
  useReconcileTransaction,
  formatBankAmount,
  type OBTransaction,
  type OBReconciliationStatus,
  type OBTransactionDirection,
} from '@business-assistant/shared';

interface OpenBankTransactionsListProps {
  /** Bank account ID */
  bankAccountId: string;
  /** Filter by date range */
  fromDate?: string;
  toDate?: string;
  /** Filter by direction */
  direction?: OBTransactionDirection;
  /** Filter by reconciliation status */
  reconciliationStatus?: OBReconciliationStatus;
  /** Callback when transaction is selected */
  onSelectTransaction?: (transaction: OBTransaction) => void;
  /** Callback when reconcile is clicked */
  onReconcile?: (transaction: OBTransaction) => void;
  /** Max items to show */
  limit?: number;
  /** Show search bar */
  showSearch?: boolean;
  /** Show filters */
  showFilters?: boolean;
  /** Compact mode */
  compact?: boolean;
}

export function OpenBankTransactionsList({
  bankAccountId,
  fromDate,
  toDate,
  direction,
  reconciliationStatus,
  onSelectTransaction,
  onReconcile,
  limit = 50,
  showSearch = true,
  showFilters = true,
  compact = false,
}: OpenBankTransactionsListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDirection, setFilterDirection] = useState<OBTransactionDirection | undefined>(direction);
  const [filterStatus, setFilterStatus] = useState<OBReconciliationStatus | undefined>(reconciliationStatus);

  const { data: transactions = [], isLoading, refetch } = useBankTransactions(
    bankAccountId,
    {
      fromDate,
      toDate,
      direction: filterDirection,
      reconciliationStatus: filterStatus,
      searchQuery: searchQuery || undefined,
      limit,
    },
    !!bankAccountId
  );

  const reconcileTransaction = useReconcileTransaction();

  const handleQuickReconcile = async (transaction: OBTransaction) => {
    if (onReconcile) {
      onReconcile(transaction);
      return;
    }

    // Quick reconcile without linking
    await reconcileTransaction.mutateAsync({
      transactionId: transaction.id,
    });
    refetch();
  };

  // Format date for display
  const formatFullDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Group transactions by date
  const transactionsByDate = transactions.reduce<Record<string, OBTransaction[]>>(
    (acc, tx) => {
      const date = tx.transactionDate.split('T')[0];
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(tx);
      return acc;
    },
    {}
  );

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          style={{ animation: 'spin 1s linear infinite', margin: '0 auto' }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <p style={{ marginTop: '8px' }}>Caricamento movimenti...</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Search and Filters */}
      {(showSearch || showFilters) && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {showSearch && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1,
                minWidth: '200px',
                padding: '8px 12px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Cerca movimenti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  fontSize: '14px',
                }}
              />
            </div>
          )}

          {showFilters && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                value={filterDirection || ''}
                onChange={(e) => setFilterDirection((e.target.value as OBTransactionDirection) || undefined)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  fontSize: '14px',
                }}
              >
                <option value="">Tutti</option>
                <option value="IN">Entrate</option>
                <option value="OUT">Uscite</option>
              </select>

              <select
                value={filterStatus || ''}
                onChange={(e) => setFilterStatus((e.target.value as OBReconciliationStatus) || undefined)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  fontSize: '14px',
                }}
              >
                <option value="">Tutti gli stati</option>
                <option value="UNRECONCILED">Da riconciliare</option>
                <option value="RECONCILED">Riconciliati</option>
                <option value="EXCLUDED">Esclusi</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {transactions.length === 0 && (
        <div
          style={{
            padding: '32px',
            textAlign: 'center',
            color: '#6b7280',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
          }}
        >
          <p>Nessun movimento trovato</p>
        </div>
      )}

      {/* Transactions List */}
      {Object.entries(transactionsByDate).map(([date, dateTxs]) => (
        <div key={date}>
          {/* Date Header */}
          <div
            style={{
              fontSize: '12px',
              fontWeight: 500,
              color: '#6b7280',
              textTransform: 'capitalize',
              marginBottom: '8px',
              padding: '0 4px',
            }}
          >
            {formatFullDate(date)}
          </div>

          {/* Transactions */}
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
            }}
          >
            {dateTxs.map((tx, index) => (
              <div
                key={tx.id}
                onClick={() => onSelectTransaction?.(tx)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: compact ? '10px 12px' : '12px 16px',
                  cursor: onSelectTransaction ? 'pointer' : 'default',
                  borderBottom: index < dateTxs.length - 1 ? '1px solid #e5e7eb' : 'none',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (onSelectTransaction) {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                  {/* Direction Icon */}
                  <div
                    style={{
                      width: compact ? '28px' : '36px',
                      height: compact ? '28px' : '36px',
                      borderRadius: '50%',
                      backgroundColor: tx.direction === 'IN' ? '#dcfce7' : '#fee2e2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {tx.direction === 'IN' ? (
                      <svg
                        width={compact ? 14 : 18}
                        height={compact ? 14 : 18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#16a34a"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 14l-7 7m0 0l-7-7m7 7V3"
                        />
                      </svg>
                    ) : (
                      <svg
                        width={compact ? 14 : 18}
                        height={compact ? 14 : 18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#dc2626"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 10l7-7m0 0l7 7m-7-7v18"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Transaction Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 500,
                        fontSize: compact ? '13px' : '14px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {tx.counterparty || tx.description}
                    </div>
                    {!compact && tx.counterparty && tx.description !== tx.counterparty && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {tx.description}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                  {/* Reconciliation Status */}
                  {tx.reconciliationStatus !== 'RECONCILED' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuickReconcile(tx);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: tx.reconciliationStatus === 'UNRECONCILED' ? '#eab308' : '#9ca3af',
                      }}
                      title="Riconcilia"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                      </svg>
                    </button>
                  )}

                  {tx.reconciliationStatus === 'RECONCILED' && (
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Riconciliato"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}

                  {/* Amount */}
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: compact ? '13px' : '15px',
                      color: tx.direction === 'IN' ? '#16a34a' : '#111827',
                      minWidth: '80px',
                      textAlign: 'right',
                    }}
                  >
                    {tx.direction === 'IN' ? '+' : '-'}
                    {formatBankAmount(tx.amount, tx.currency)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default OpenBankTransactionsList;
